import type { Network } from './network';
import { positionAlong } from './route';

type Pos = [number, number];
type Ring = number[][];

/** Metres between two positions (good enough within a city). */
export function metres(a: Pos, b: Pos): number {
  const k = Math.cos((a[1] * Math.PI) / 180);
  return Math.hypot((a[0] - b[0]) * k * 111_320, (a[1] - b[1]) * 110_540);
}

const centroid = (ring: Ring): Pos => {
  const pts = ring.slice(0, -1);
  return [pts.reduce((s, p) => s + p[0]!, 0) / pts.length, pts.reduce((s, p) => s + p[1]!, 0) / pts.length];
};

function contains(ring: Ring, p: Pos): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = [ring[i]![0]!, ring[i]![1]!];
    const [xj, yj] = [ring[j]![0]!, ring[j]![1]!];
    if (yi > p[1] !== yj > p[1] && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

type Line = Network['lines'][number];
type Station = Network['stations'][number];

/** Line coordinates in travel order (lines are not always drawn from their first station). */
export function travelOrdered(line: Line, stations: Map<string, Station>): Pos[] {
  const coords = line.geometry.coordinates as Pos[];
  const first = stations.get(line.ordered_stations[0] ?? '');
  const last = stations.get(line.ordered_stations.at(-1) ?? '');
  if (!first || !last || coords.length < 2) return coords;
  const a = positionAlong(coords, first.geometry.coordinates);
  const b = positionAlong(coords, last.geometry.coordinates);
  return a <= b ? coords : [...coords].reverse();
}

export interface Terminus {
  pos: Pos;
  name: string;
}
export interface FenceStatus {
  centre: Pos;
  /** Contains its terminus or lies within NEAR_M of it. */
  ok: boolean;
  /** Lies at the other terminus: the two fences are probably swapped. */
  swapped: boolean;
  metresToOwn: number | null;
}
export interface ZoneCheck {
  /** A: where the going line starts (first station of the going line). */
  a?: Terminus;
  /** B: where the going line ends (its last station); the return line starts here. */
  b?: Terminus;
  going?: FenceStatus;
  return?: FenceStatus;
  /** The return line starts near B and ends near A. Null when it cannot be told. */
  returnMatches: boolean | null;
}

const NEAR_M = 300;

function fenceStatus(ring: Ring | undefined, own?: Terminus, other?: Terminus): FenceStatus | undefined {
  if (!ring?.length) return undefined;
  const centre = centroid(ring);
  if (!own) return { centre, ok: true, swapped: false, metresToOwn: null };
  const dOwn = contains(ring, own.pos) ? 0 : metres(centre, own.pos);
  const dOther = other ? (contains(ring, other.pos) ? 0 : metres(centre, other.pos)) : Infinity;
  return {
    centre,
    ok: dOwn <= NEAR_M,
    swapped: dOwn > NEAR_M && dOther <= NEAR_M,
    metresToOwn: Math.round(dOwn),
  };
}

/** Where A and B are and whether each fence sits at the terminus it belongs to. */
export function checkZone(
  values: {
    on_line_going?: string | null;
    on_line_return?: string | null;
    geometry_going?: { coordinates: Ring[] } | null;
    geometry_return?: { coordinates: Ring[] } | null;
  },
  network: Network,
): ZoneCheck {
  const stations = new Map(network.stations.map((s) => [s._id, s]));
  const terminus = (id?: string): Terminus | undefined => {
    const s = id ? stations.get(id) : undefined;
    return s ? { pos: s.geometry.coordinates as Pos, name: s.name } : undefined;
  };
  const going = network.lines.find((l) => l._id === values.on_line_going);
  const ret = network.lines.find((l) => l._id === values.on_line_return);
  const a = terminus(going?.ordered_stations[0]) ?? terminus(ret?.ordered_stations.at(-1));
  const b = terminus(going?.ordered_stations.at(-1)) ?? terminus(ret?.ordered_stations[0]);
  const retStart = terminus(ret?.ordered_stations[0]);
  const retEnd = terminus(ret?.ordered_stations.at(-1));
  return {
    a,
    b,
    going: fenceStatus(values.geometry_going?.coordinates[0], a, b),
    return: fenceStatus(values.geometry_return?.coordinates[0], b, a),
    returnMatches:
      going && retStart && retEnd && a && b
        ? metres(retStart.pos, b.pos) < 800 && metres(retEnd.pos, a.pos) < 800
        : null,
  };
}
